import React, { useState, useMemo, useCallback } from 'react';
import { useBts } from '@/context/btsContext/BtsContextProvider';
import { FaRegEdit, FaTrash } from 'react-icons/fa';

// --- Row Component ---
const ProductRow = React.memo(function ProductRow({
  row,
  idx,
  tableHeadersLength,
  selectedIds,
  handleSelectRow,
  handleEdit,
  handleDelete,
}) {
  const qty = Number(row.quantity) || 0;
  const unitCost = Number(row.price) / (Number(row.conversionFactor) || 1);
  const exSell = Number(row.exSell) || 0;
  const incSell = exSell * 1.1;

  const totalExSell = exSell * qty;
  const totalIncSell = incSell * qty;
  const totalAvgCost = unitCost * qty;

  const gpDollar = totalExSell - totalAvgCost;
  const gpPercent = totalExSell !== 0 ? (gpDollar / totalExSell) * 100 : 0;

  return (
    <>
      {row.header && (
        <tr className="bg-blue-100 text-xs border-b border-gray-200">
          <td
            colSpan={tableHeadersLength}
            className="px-2 py-2 text-left font-bold text-blue-800">
            {row.header}
          </td>
        </tr>
      )}
      <tr className="bg-white text-sm border-b border-gray-200">
        <td className="px-2 py-2">
          <input
            type="checkbox"
            checked={selectedIds.includes(row.id)}
            onChange={() => handleSelectRow(row.id)}
          />
        </td>
        <td className="px-2 py-2">{row.sequenceNumber}</td>
        <td className="px-2 py-2 text-xs text-left">{row.itemCode}</td>

        <td className="px-2 py-2 text-sm min-w-40 text-left">
          {row.description}
        </td>
        <td className="px-2 py-2">{row.unit}</td>
        <td className="px-2 py-2">{row.quantity}</td>
        <td className="px-2 py-2">{unitCost.toFixed(2)}</td>
        <td className="px-2 py-2">{gpPercent.toFixed(2)}%</td>
        <td className="px-2 py-2">{exSell.toFixed(2)}</td>
        <td className="px-2 py-2">{incSell.toFixed(2)}</td>
        <td className="px-2 py-2">{totalIncSell.toFixed(2)}</td>
        <td className="px-2 py-2 whitespace-nowrap text-sm font-medium">
          <div className="flex items-center space-x-2 justify-center">
            <button
              type="button"
              title="Edit"
              className="text-blue-600 hover:text-blue-900"
              onClick={() => handleEdit(row)}>
              <FaRegEdit size={18} />
            </button>
            <button
              type="button"
              title="Delete"
              className="text-red-600 hover:text-red-900"
              onClick={() => handleDelete(row)}>
              <FaTrash className="text-lg" />
            </button>
          </div>
        </td>
      </tr>
    </>
  );
});

// --- Main Table ---
const ProposalProductTable = ({ proposalId }) => {
  const {
    btsClassProdData,
    setBtsProduct,
    deleteClassProduct,
    fetchBtsProductsByClass,
    deleteMultipleProductByClass,
  } = useBts();

  const [selectedIds, setSelectedIds] = useState([]);

  // Bulk delete
  const getClassId = () => {
    if (!selectedIds.length) return null;
    const row = (btsClassProdData || []).find((r) => r.id === selectedIds[0]);
    return row ? row.btsClassId : null;
  };

  const handleBulkDelete = async () => {
    if (!selectedIds.length) return;
    const classId = getClassId();
    if (!classId) return;
    try {
      await deleteMultipleProductByClass(proposalId, classId, [...selectedIds]);
      setSelectedIds([]);
      await fetchBtsProductsByClass(proposalId, classId);
    } catch {}
  };

  const handleSelectAll = () => {
    if (selectedIds.length === (btsClassProdData || []).length) {
      setSelectedIds([]);
    } else {
      setSelectedIds((btsClassProdData || []).map((row) => row.id));
    }
  };

  const handleSelectRow = useCallback(
    (id) => {
      setSelectedIds((prev) =>
        prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
      );
    },
    [setSelectedIds],
  );

  const handleEdit = useCallback(
    (row) => {
      setBtsProduct(row);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [setBtsProduct],
  );

  const handleDelete = useCallback(
    async (row) => {
      try {
        await deleteClassProduct(proposalId, row.btsClassId, row.id);
      } catch {}
    },
    [deleteClassProduct, proposalId],
  );

  // --- Footer Totals ---
  const totals = useMemo(() => {
    let totalExSell = 0;
    let totalIncSell = 0;
    let totalAvgCost = 0;

    (btsClassProdData || []).forEach((row) => {
      const qty = Number(row.quantity) || 0;
      const unitCost = Number(row.price) / (Number(row.conversionFactor) || 1);
      const exSell = Number(row.exSell) || 0;
      const incSell = exSell * 1.1;

      totalExSell += exSell * qty;
      totalIncSell += incSell * qty;
      totalAvgCost += unitCost * qty;
    });

    const totalGpDollar = totalExSell - totalAvgCost;
    const gpPercent =
      totalExSell !== 0 ? (totalGpDollar / totalExSell) * 100 : 0;

    return { totalExSell, totalIncSell, totalGpDollar, gpPercent };
  }, [btsClassProdData]);

  const tableHeaders = [
    '',
    'Seq No',
    'Item Code',
    'Description',
    'UOM',
    'Qty',
    'BTS Cost',
    'GP %',
    'Ex-Sell',
    'Inc-Sell',
    'Total',
    'Actions',
  ];

  const rowCount = btsClassProdData?.length || 0;
  const scrollNeeded = rowCount > 8 || rowCount * 50 > window.innerHeight * 0.7;

  const tableRows = useMemo(() => {
    if (
      !btsClassProdData ||
      btsClassProdData.length === 0 ||
      btsClassProdData[0] === 'No Data Found'
    ) {
      return (
        <tr>
          <td
            colSpan={tableHeaders.length}
            className="text-center py-4 text-gray-500">
            No Data Found
          </td>
        </tr>
      );
    }
    return btsClassProdData.map((row, idx) => (
      <ProductRow
        key={row.id || idx}
        row={row}
        idx={idx}
        tableHeadersLength={tableHeaders.length}
        selectedIds={selectedIds}
        handleSelectRow={handleSelectRow}
        handleEdit={handleEdit}
        handleDelete={handleDelete}
      />
    ));
  }, [
    btsClassProdData,
    selectedIds,
    handleSelectRow,
    handleEdit,
    handleDelete,
    tableHeaders.length,
  ]);

  return (
    <div className="container mx-auto mb-5 mt-5 pr-5">
      <div className="flex justify-end mb-2 mt-2">
        <button
          className="bg-red-500 text-white px-4 py-1 rounded disabled:opacity-50"
          onClick={handleBulkDelete}
          disabled={
            selectedIds.length === 0 ||
            (Array.isArray(btsClassProdData) &&
              btsClassProdData[0] === 'No Data Found')
          }>
          Delete Selected
        </button>
      </div>
      <div
        className={`relative ${scrollNeeded ? 'max-h-[70vh] overflow-y-auto' : ''}`}>
        <table className="min-w-full bg-white border-gray-200">
          <thead
            className={`bg-gray-200 font-400 text-left min-w-full ${scrollNeeded ? 'sticky top-0 z-10' : ''}`}>
            <tr>
              {tableHeaders.map((header, idx) => (
                <th
                  key={idx}
                  className="px-2 py-3 border-b text-left text-xs font-medium text-gray-500 uppercase">
                  {idx === 0 ? (
                    <input
                      type="checkbox"
                      checked={
                        (btsClassProdData || []).length > 0 &&
                        selectedIds.length === (btsClassProdData || []).length
                      }
                      onChange={handleSelectAll}
                      disabled={(btsClassProdData?.length || 0) === 0}
                    />
                  ) : (
                    header
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="text-center min-w-full font-600">{tableRows}</tbody>
        </table>
      </div>

      {/* --- Footer Totals --- */}
      <div className="flex justify-end gap-10 mt-2 pr-10 text-lg font-semibold ">
        <div>GP: {totals.gpPercent.toFixed(2)}%</div>
        <div>Total GP: ${totals.totalGpDollar.toFixed(2)}</div>
        <div>Total Sum: ${totals.totalIncSell.toFixed(2)}</div>
      </div>
    </div>
  );
};

export default ProposalProductTable;
